import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  IconButton,
  Collapse,
  Grid
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Security as SecurityIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from '../services/api';
import { Download as DownloadIcon } from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';


function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    action: '',
    userId: '',
    startDate: null,
    endDate: null
  });
  const [expandedRows, setExpandedRows] = useState({});

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...filters
      };
      
      // Format dates
      if (params.startDate) {
        params.startDate = params.startDate.toISOString().split('T')[0];
      }
      if (params.endDate) {
        params.endDate = params.endDate.toISOString().split('T')[0];
      }
      
      const response = await api.get('/audit/logs', { params });
      setLogs(response.data.logs);
      setTotal(response.data.pagination.total);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [page, rowsPerPage, filters]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const toggleRowExpanded = (logId) => {
    setExpandedRows(prev => ({
      ...prev,
      [logId]: !prev[logId]
    }));
  };

  const getActionColor = (action) => {
    if (action.includes('DELETE')) return 'error';
    if (action.includes('CREATE')) return 'success';
    if (action.includes('UPDATE') || action.includes('RESET')) return 'warning';
    if (action.includes('LOGIN')) return 'info';
    return 'default';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };


const handleExportCSV = async () => {
  try {
    // Clean up filters - remove null values
    const cleanFilters = {};
    Object.keys(filters).forEach(key => {
      if (filters[key] && filters[key] !== null && filters[key] !== '') {
        if (key === 'startDate' || key === 'endDate') {
          cleanFilters[key] = filters[key].toISOString().split('T')[0];
        } else {
          cleanFilters[key] = filters[key];
        }
      }
    });
    
    const params = new URLSearchParams({
      format: 'csv',
      ...cleanFilters
    });
    
    const response = await api.get(`/audit/logs/export?${params}`, {
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `audit_logs_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    console.error('Error exporting CSV:', error);
  }
};

const handleExportPDF = async () => {
  try {
    // Clean up filters - remove null values
    const cleanFilters = {};
    Object.keys(filters).forEach(key => {
      if (filters[key] && filters[key] !== null && filters[key] !== '') {
        if (key === 'startDate' || key === 'endDate') {
          cleanFilters[key] = filters[key].toISOString().split('T')[0];
        } else {
          cleanFilters[key] = filters[key];
        }
      }
    });
    
    const params = new URLSearchParams({
      format: 'json',
      ...cleanFilters
    });
    
    const response = await api.get(`/audit/logs/export?${params}`);
    const data = response.data;
    
    // Create PDF
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(16);
    doc.text('Audit Logs Report', 14, 15);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 25);
    
    // Add filters info if any
    let yPos = 25;
    if (cleanFilters.action) {
      doc.text(`Action: ${cleanFilters.action}`, 14, yPos += 10);
    }
    if (cleanFilters.startDate || cleanFilters.endDate) {
      doc.text(`Date Range: ${cleanFilters.startDate || 'Start'} to ${cleanFilters.endDate || 'End'}`, 14, yPos += 10);
    }
    
    // Simple alternative without autoTable - just list the data
    yPos += 15;
    doc.setFontSize(8);
    
    // Headers
    doc.text('Date/Time | User | Action | Entity | IP Address', 14, yPos);
    yPos += 5;
    doc.line(14, yPos, 196, yPos); // horizontal line
    yPos += 5;
    
    // Data rows
    data.slice(0, 40).forEach((row, index) => { // Limit to 40 rows for first page
      if (yPos > 270) { // Check if we need a new page
        doc.addPage();
        yPos = 20;
      }
      
      const rowText = [
        new Date(row.created_at).toLocaleDateString(),
        row.user_username || 'Unknown',
        row.action.replace(/_/g, ' '),
        `${row.entity_type || ''} #${row.entity_id || ''}`,
        row.ip_address || ''
      ].join(' | ');
      
      doc.text(rowText, 14, yPos);
      yPos += 5;
    });
    
    if (data.length > 40) {
      doc.text(`... and ${data.length - 40} more entries`, 14, yPos + 5);
    }
    
    doc.save(`audit_logs_${new Date().toISOString().split('T')[0]}.pdf`);
  } catch (error) {
    console.error('Error exporting PDF:', error);
  }
};

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SecurityIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4">Audit Logs</Typography>
        </Box>
        <IconButton onClick={fetchLogs} disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </Box>

<Box sx={{ display: 'flex', gap: 1 }}>
  <Button
    startIcon={<DownloadIcon />}
    onClick={handleExportCSV}
    variant="outlined"
    size="small"
  >
    Export CSV
  </Button>
  <Button
    startIcon={<DownloadIcon />}
    onClick={handleExportPDF}
    variant="outlined"
    size="small"
  >
    Export PDF
  </Button>
  <IconButton onClick={fetchLogs} disabled={loading}>
    <RefreshIcon />
  </IconButton>
</Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Action</InputLabel>
              <Select
                value={filters.action}
                label="Action"
                onChange={(e) => handleFilterChange('action', e.target.value)}
              >
                <MenuItem value="">All Actions</MenuItem>
                <MenuItem value="USER_LOGIN">User Login</MenuItem>
                <MenuItem value="USER_LOGOUT">User Logout</MenuItem>
                <MenuItem value="PASSWORD_RESET_BY_ADMIN">Password Reset</MenuItem>
                <MenuItem value="PASSWORD_CHANGED_BY_USER">Password Change</MenuItem>
                <MenuItem value="USER_CREATED">User Created</MenuItem>
                <MenuItem value="USER_UPDATED">User Updated</MenuItem>
                <MenuItem value="USER_DELETED">User Deleted</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="User ID"
              value={filters.userId}
              onChange={(e) => handleFilterChange('userId', e.target.value)}
              type="number"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={filters.startDate}
                onChange={(value) => handleFilterChange('startDate', value)}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={filters.endDate}
                onChange={(value) => handleFilterChange('endDate', value)}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      </Paper>

      {/* Logs Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Date/Time</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Details</TableCell>
              <TableCell>IP Address</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log) => (
              <React.Fragment key={log.id}>
                <TableRow hover>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => toggleRowExpanded(log.id)}
                    >
                      {expandedRows[log.id] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                  </TableCell>
                  <TableCell>{formatDate(log.created_at)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={log.action.replace(/_/g, ' ')} 
                      color={getActionColor(log.action)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {log.user_username || `User ID: ${log.user_id}`}
                  </TableCell>
                  <TableCell>
                    {log.action === 'PASSWORD_RESET_BY_ADMIN' && 
                      `Reset by: ${log.new_values?.reset_by || 'Unknown'}`
                    }
                    {log.entity_type && ` (${log.entity_type} #${log.entity_id})`}
                  </TableCell>
                  <TableCell>{log.ip_address}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={expandedRows[log.id]} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          Additional Details
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2">Old Values:</Typography>
                            <pre style={{ 
                              backgroundColor: '#f5f5f5', 
                              padding: '10px', 
                              borderRadius: '4px',
                              overflow: 'auto'
                            }}>
                              {JSON.stringify(log.old_values, null, 2)}
                            </pre>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2">New Values:</Typography>
                            <pre style={{ 
                              backgroundColor: '#f5f5f5', 
                              padding: '10px', 
                              borderRadius: '4px',
                              overflow: 'auto'
                            }}>
                              {JSON.stringify(log.new_values, null, 2)}
                            </pre>
                          </Grid>
                        </Grid>
                        {log.user_agent && (
                          <Box mt={2}>
                            <Typography variant="subtitle2">User Agent:</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {log.user_agent}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
}

export default AuditLogs;
